import React, { useEffect, useState } from "react";
import {
    Plus,
    Search,
    X,
    Edit2,
    ListRestart,
    Trash2,
    Users
} from "lucide-react";
import PartnerForm from "../components/forms/PartnerForm";
import ConfirmDialog from "../components/common/ConfirmDialog";
import PageHeader from "../components/common/PageHeader";
import DataTable from "../components/common/DataTable";
import EmptyState from "../components/common/EmptyState";
import Pagination from "../components/common/Pagination";
import useAlert from "../components/hook/useAlert";
import callApi from "../helpers/networkHelper";
import API_CONFIG from "../config/apiConstant";
import { parsePhoneNumberFromString } from "libphonenumber-js";

// CONSTANTS
const PAGE_SIZE = 10;

// HELPERS
const buildQueryString = (params) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== "") {
            query.append(key, value);
        }
    });
    return query.toString();
};

const getReadableErrorMessage = (error) => {
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.message) return error.message;
    return "Something went wrong. Please try again.";
};

// Phone formatter for DISPLAY ONLY
const formatPhone = (phone) => {
    if (!phone) return "—";
    const phoneObj = parsePhoneNumberFromString(phone);
    return phoneObj ? phoneObj.formatInternational() : phone;
};


// MAIN COMPONENT
const Partner = () => {
    const { showSuccess, showError } = useAlert();

    const [partners, setPartners] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPartner, setEditingPartner] = useState(null);
    const [deletingPartner, setDeletingPartner] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const totalPages = Math.ceil(total / PAGE_SIZE);

    // FETCH PARTNERS
    const fetchPartners = async (initial = false) => {
        initial ? setIsPageLoading(true) : setIsTableLoading(true);

        try {
            const queryString = buildQueryString({
                page,
                limit: PAGE_SIZE,
                search,
                isActive:
                    statusFilter === "all"
                        ? undefined
                        : statusFilter === "active"
            });

            const res = await callApi(
                API_CONFIG.PARTNER.GET.METHOD,
                `${API_CONFIG.PARTNER.GET.ENDPOINT}?${queryString}`
            );

            setPartners(res.data.data);
            setTotal(res.data.total);
        } catch (e) {
            showError(getReadableErrorMessage(e));
        } finally {
            setIsPageLoading(false);
            setIsTableLoading(false);
        }
    };

    useEffect(() => {
        fetchPartners(true);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!isPageLoading) fetchPartners(false);
        // eslint-disable-next-line
    }, [page, search, statusFilter]);

    // CREATE / UPDATE
    const handleSubmitPartner = async (data) => {
        setIsSubmitting(true);
        try {
            if (editingPartner) {
                await callApi(
                    API_CONFIG.PARTNER.UPDATE.METHOD,
                    API_CONFIG.PARTNER.UPDATE.ENDPOINT.replace(":id", editingPartner._id),
                    data
                );
                showSuccess("Partner updated successfully");
            } else {
                await callApi(
                    API_CONFIG.PARTNER.CREATE.METHOD,
                    API_CONFIG.PARTNER.CREATE.ENDPOINT,
                    data
                );
                showSuccess("Partner created successfully");
            }

            setIsModalOpen(false);
            setEditingPartner(null);
            fetchPartners();
        } catch (err) {
            showError(getReadableErrorMessage(err));
        } finally {
            setIsSubmitting(false);
        }
    };

    // TOGGLE STATUS 
    const toggleStatus = async (partner) => {
        try {
            await callApi(
                API_CONFIG.PARTNER.TOGGLE_STATUS.METHOD,
                API_CONFIG.PARTNER.TOGGLE_STATUS.ENDPOINT.replace(
                    ":id",
                    partner._id
                ),
                { isActive: !partner.isActive }
            );
            showSuccess("Partner status updated");
            fetchPartners();
        } catch (e) {
            showError(getReadableErrorMessage(e));
        }
    };

    // DELETE PARTNER
    const handleDeletePartner = async () => {
        if (!deletingPartner) return;

        setIsDeleting(true);
        try {
            await callApi(
                API_CONFIG.PARTNER.DELETE.METHOD,
                API_CONFIG.PARTNER.DELETE.ENDPOINT.replace(
                    ":id",
                    deletingPartner._id
                )
            );

            showSuccess(`Partner "${deletingPartner.partnerName}" deleted`);
            setDeletingPartner(null);

            // Removing the last row on a page would otherwise leave the table
            // empty on a page that no longer exists; stepping back refetches.
            if (partners.length === 1 && page > 1) {
                setPage(page - 1);
            } else {
                fetchPartners();
            }
        } catch (e) {
            // The backend refuses while licenses still reference the partner, so
            // the dialog stays open with the reason showing.
            showError(getReadableErrorMessage(e));
        } finally {
            setIsDeleting(false);
        }
    };

    const hasActiveFilters = search.trim() !== "" || statusFilter !== "all";

    const clearFilters = () => {
        setSearch("");
        setStatusFilter("all");
        setPage(1);
    };

    const columns = [
        {
            key: "partnerID",
            label: "Partner ID",
            nowrap: true,
            render: (partner) => (
                <span className="font-medium" style={{ color: 'var(--brand)' }}>
                    {partner.partnerID}
                </span>
            ),
        },
        {
            key: "partnerName",
            label: "Partner Name",
            render: (partner) => partner.partnerName,
        },
        {
            key: "poc",
            label: "Contact Person",
            render: (partner) => (
                <span style={{ color: 'var(--main-text-muted)' }}>{partner.poc?.name || "—"}</span>
            ),
        },
        {
            key: "email",
            label: "Email",
            render: (partner) => (
                <span style={{ color: 'var(--main-text-muted)' }}>{partner.poc?.email || "—"}</span>
            ),
        },
        {
            key: "phone",
            label: "Phone",
            nowrap: true,
            render: (partner) => (
                <span style={{ color: 'var(--main-text-muted)' }}>
                    {formatPhone(partner.poc?.number)}
                </span>
            ),
        },
        {
            key: "country",
            label: "Country",
            nowrap: true,
            render: (partner) => (
                <span style={{ color: 'var(--main-text-muted)' }}>
                    {partner.location?.country || "—"}
                </span>
            ),
        },
        {
            key: "status",
            label: "Status",
            nowrap: true,
            render: (partner) => (
                <div className="flex items-center gap-3">
                    <button
                        disabled={isTableLoading}
                        onClick={() => toggleStatus(partner)}
                        role="switch"
                        aria-checked={partner.isActive}
                        aria-label={partner.isActive ? "Deactivate partner" : "Activate partner"}
                        title={partner.isActive ? "Deactivate partner" : "Activate partner"}
                        className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-300"
                        style={{ background: partner.isActive ? 'var(--success)' : 'var(--danger)' }}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${
                                partner.isActive ? "translate-x-6" : "translate-x-1"
                            }`}
                        />
                    </button>

                    <span
                        className="text-xs font-medium min-w-[56px]"
                        style={{ color: partner.isActive ? 'var(--success)' : 'var(--danger)' }}
                    >
                        {partner.isActive ? "Active" : "Inactive"}
                    </span>
                </div>
            ),
        },
        {
            key: "actions",
            label: "Actions",
            nowrap: true,
            align: "right",
            render: (partner) => (
                <div className="flex items-center justify-end gap-4">
                    <button
                        disabled={!partner.isActive}
                        aria-label={`Edit ${partner.partnerName}`}
                        title={partner.isActive ? "Edit partner" : "Activate the partner to edit it"}
                        onClick={() => {
                            if (!partner.isActive) return;
                            setEditingPartner(partner);
                            setIsModalOpen(true);
                        }}
                        className={partner.isActive ? "font-medium" : "opacity-40 cursor-not-allowed"}
                        style={{ color: partner.isActive ? 'var(--brand)' : 'var(--main-text-muted)' }}
                    >
                        <Edit2 size={16} />
                    </button>

                    {/* Allowed regardless of active state — deactivating is
                        the reversible action, so an inactive partner is the
                        one most likely to be deleted. */}
                    <button
                        aria-label={`Delete ${partner.partnerName}`}
                        title="Delete partner"
                        onClick={() => setDeletingPartner(partner)}
                        style={{ color: 'var(--danger)' }}
                        className="hover:opacity-80"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="p-4 sm:p-6 min-h-screen w-full" style={{ background: 'var(--main-bg)', color: 'var(--main-text)' }}>

            <PageHeader
                icon={<Users size={28} strokeWidth={2.2} />}
                title="Partners"
                subtitle="Create, update and manage partners"
                meta={isPageLoading ? null : `${total} ${total === 1 ? "partner" : "partners"}`}
                actions={
                    <button
                        onClick={() => {
                            setEditingPartner(null);
                            setIsModalOpen(true);
                        }}
                        className="flex items-center gap-2 font-medium px-4 h-10 rounded-lg transition hover:opacity-90"
                        style={{ background: 'var(--brand)', color: '#fff' }}
                    >
                        <Plus size={18} />
                        Create Partner
                    </button>
                }
            />

            {/* Filters */}
            <div className="p-4 sm:p-5 rounded-lg mb-6 border" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                <div className="flex flex-col sm:flex-row sm:items-end gap-4">

                    {/* Search */}
                    <div className="flex-1 min-w-0">
                        <label htmlFor="partner-search" className="text-sm mb-2 block" style={{ color: 'var(--main-text)' }}>
                            Search
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-2.5 pointer-events-none" size={18} style={{ color: 'var(--main-text-muted)' }} />
                            <input
                                id="partner-search"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full pl-10 pr-9 py-2 h-10 border rounded-lg"
                                style={{
                                    background: 'var(--input-background)',
                                    color: 'var(--main-text)',
                                    borderColor: 'var(--card-border)'
                                }}
                                placeholder="Search by partner name, contact name or email"
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => { setSearch(""); setPage(1); }}
                                    aria-label="Clear search"
                                    className="absolute right-2 top-2.5 p-0.5 rounded hover:opacity-70"
                                    style={{ color: 'var(--main-text-muted)' }}
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Status */}
                    <div>
                        <label htmlFor="partner-status" className="text-sm mb-2 block" style={{ color: 'var(--main-text)' }}>
                            Status
                        </label>
                        <select
                            id="partner-status"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            className="w-full sm:w-[140px] px-3 py-2 h-10 border rounded-lg"
                            style={{
                                background: 'var(--input-background)',
                                color: 'var(--main-text)',
                                borderColor: 'var(--card-border)'
                            }}
                        >
                            <option value="all">All</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>

                    {/* Reset — disabled when there is nothing to reset. */}
                    <button
                        onClick={clearFilters}
                        disabled={!hasActiveFilters}
                        className="flex items-center justify-center gap-2 h-10 px-4 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{
                            background: 'var(--brand-light)',
                            color: 'var(--brand)'
                        }}
                    >
                        <ListRestart size={16} />
                        Reset
                    </button>
                </div>
            </div>

            <DataTable
                columns={columns}
                rows={partners}
                loading={isPageLoading || isTableLoading}
                getRowKey={(partner) => partner._id}
                emptyState={
                    hasActiveFilters ? (
                        <EmptyState
                            icon={<Search size={26} />}
                            title="No partners match these filters"
                            description="Try a different search term, or clear the filters to see every partner."
                            action={
                                <button
                                    onClick={clearFilters}
                                    className="h-10 px-4 rounded-lg font-medium border transition hover:opacity-80"
                                    style={{ color: 'var(--brand)', borderColor: 'var(--brand)' }}
                                >
                                    Clear filters
                                </button>
                            }
                        />
                    ) : (
                        <EmptyState
                            icon={<Users size={26} />}
                            title="No partners yet"
                            description="Partners are the resellers and integrators you issue licenses through."
                            action={
                                <button
                                    onClick={() => {
                                        setEditingPartner(null);
                                        setIsModalOpen(true);
                                    }}
                                    className="flex items-center gap-2 h-10 px-4 rounded-lg font-medium"
                                    style={{ background: 'var(--brand)', color: '#fff' }}
                                >
                                    <Plus size={18} /> Create Partner
                                </button>
                            }
                        />
                    )
                }
            />

            <Pagination
                page={page}
                totalPages={totalPages}
                totalRecords={total}
                onPageChange={setPage}
            />


            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
                    <div className="rounded-lg w-full max-w-xl p-6 max-h-[85vh]" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                        <div className="flex justify-between mb-4">
                            <h3 className="text-lg font-semibold" style={{ color: 'var(--main-text)' }}>
                                {editingPartner ? "Edit Partner" : "Create Partner"}
                            </h3>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setEditingPartner(null);
                                }}
                                className="hover:text-[var(--main-text)]"
                                style={{ color: 'var(--main-text-muted)' }}
                            >
                                <X />
                            </button>
                        </div>

                        <div
                            style={{ scrollbarGutter: "stable" }}
                            className="max-h-[70vh] overflow-y-auto pr-6 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"
                        >
                            <PartnerForm
                                onSubmit={handleSubmitPartner}
                                isLoading={isSubmitting}
                                defaultValues={editingPartner}
                                submitLabel={
                                    editingPartner ? "Update Partner" : "Create Partner"
                                }
                            />
                        </div>

                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={Boolean(deletingPartner)}
                title="Delete partner"
                message={
                    <>
                        Permanently delete{" "}
                        <strong style={{ color: "var(--main-text)" }}>
                            {deletingPartner?.partnerName}
                        </strong>
                        ? Deactivate the partner instead if you only want to stop new
                        licenses being issued through them.
                    </>
                }
                details={
                    deletingPartner && (
                        <div className="space-y-1">
                            <div>Partner ID: {deletingPartner.partnerID}</div>
                            <div>Contact: {deletingPartner.poc?.email}</div>
                        </div>
                    )
                }
                confirmLabel="Delete partner"
                isLoading={isDeleting}
                onConfirm={handleDeletePartner}
                onCancel={() => setDeletingPartner(null)}
            />
        </div>
    );
};

export default Partner;
